import { cva, type VariantProps } from 'class-variance-authority';
import { CircleAlertIcon, CircleCheckIcon, InfoIcon, TriangleAlertIcon } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/shared/helpers';

const alertVariants = cva(
  "group/alert relative grid w-full grid-cols-[1fr] gap-0.5 rounded-lg py-3 pr-4 pl-5 text-left text-sm text-foreground before:absolute before:inset-y-2 before:left-1.5 before:w-[3px] before:rounded-full has-data-[slot=alert-action]:grid-cols-[1fr_auto] has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-2 has-[>svg]:has-data-[slot=alert-action]:grid-cols-[auto_1fr_auto] *:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg:not([class*='size-'])]:size-4",
  {
    // intents reuse the state token pairs: info→progress, warning→observe, error→alert
    variants: {
      variant: {
        info: 'bg-state-progress before:bg-state-progress-foreground *:[svg]:text-state-progress-foreground',
        warning:
          'bg-state-observe before:bg-state-observe-foreground *:[svg]:text-state-observe-foreground',
        error:
          'bg-state-alert before:bg-state-alert-foreground *:[svg]:text-state-alert-foreground',
        success:
          'bg-state-success before:bg-state-success-foreground *:[svg]:text-state-success-foreground',
      },
    },
    defaultVariants: {
      variant: 'info',
    },
  },
);

// filled treatment: glyph and outer stroke use the alert background so no halo appears
const variantIcons = {
  info: [InfoIcon, 'stroke-state-progress'],
  warning: [TriangleAlertIcon, 'stroke-state-observe'],
  error: [CircleAlertIcon, 'stroke-state-alert'],
  success: [CircleCheckIcon, 'stroke-state-success'],
} as const;

function Alert({
  className,
  variant,
  icon,
  children,
  ...props
}: React.ComponentProps<'div'> &
  VariantProps<typeof alertVariants> & {
    // defaults to the variant icon; pass a node to replace it or false to omit it
    icon?: React.ReactNode;
  }) {
  const [DefaultIcon, defaultIconClassName] = variantIcons[variant ?? 'info'];

  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      {icon ?? <DefaultIcon className={cn('fill-current', defaultIconClassName)} />}
      {children}
    </div>
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'font-medium group-has-[>svg]/alert:col-start-2 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground',
        className,
      )}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        // explicit column keeps auto-placement from drifting into the action column
        'col-start-1 text-xs text-balance text-muted-foreground group-has-[>svg]/alert:col-start-2 md:text-pretty [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4',
        className,
      )}
      {...props}
    />
  );
}

function AlertAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-action"
      className={cn('-col-start-2 row-span-2 row-start-1 self-center justify-self-end', className)}
      {...props}
    />
  );
}

export { Alert, AlertTitle, AlertDescription, AlertAction };
